// ============================================
// FILE: apiSetup/axiosInterceptor.ts
// ============================================
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { getApiBaseUrl } from './apiConfig';

const COOKIE_OPTIONS = {
  httpOnly: false,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax' as const,
  path: '/',
} as const;

// Create axios instance with dynamic base URL
export const apiClient = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Update base URL dynamically before each request
apiClient.interceptors.request.use(
  (config) => {
    // Always get fresh URL from localStorage
    config.baseURL = getApiBaseUrl();
    console.log('🌐 API Base URL:', config.baseURL); // add this
    console.log('🌐 Full URL:', `${config.baseURL}${config.url}`); // add this
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) return true;
    
    const payload = JSON.parse(atob(tokenParts[1]));
    if (!payload.exp) return true;
    
    // Add 10 second buffer to refresh before actual expiry
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    const isExpired = currentTime >= (expiryTime - 10000);
    
    if (isExpired) {
      console.log('⏰ Token expired at:', new Date(expiryTime).toLocaleString());
      console.log('⏰ Current time:', new Date(currentTime).toLocaleString());
    }
    
    return isExpired;
  } catch (e) {
    console.error('❌ Failed to decode token:', e);
    return true;
  }
};

// Helper function to clear auth and redirect
const clearAuthAndRedirect = () => {
  console.log('🚪 Clearing auth and redirecting to login...');
  Cookies.remove('accessToken', COOKIE_OPTIONS);
  Cookies.remove('refreshToken', COOKIE_OPTIONS);
  
  // Only redirect if not already on login page
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

// Helper function to refresh token
const refreshAccessToken = async (): Promise<string> => {
  const refreshTokenValue = Cookies.get('refreshToken');
  
  if (!refreshTokenValue) {
    console.error('❌ No refresh token available');
    throw new Error('No refresh token available');
  }

  console.log('🔄 Refreshing access token...');
  const baseUrl = getApiBaseUrl();
  console.log('🔄 API URL:', `${baseUrl}/refresh`);
  
  try {
    const response = await axios.post(`${baseUrl}/refresh`, {
      refresh_token: refreshTokenValue
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    console.log('📦 Refresh response status:', response.status);
    
    // Handle response structure from your API
    const { success, access_token, refresh_token } = response.data;
    
    // More lenient validation - just check if we got tokens
    if (!access_token) {
      console.error('❌ No access token in response:', response.data);
      throw new Error('No access token received');
    }
    
    console.log('✅ Token refreshed successfully');
    
    // Update cookies
    Cookies.set('accessToken', access_token, COOKIE_OPTIONS);
    if (refresh_token) {
      Cookies.set('refreshToken', refresh_token, COOKIE_OPTIONS);
    }
    
    return access_token;
  } catch (error: any) {
    console.error('❌ Token refresh error:', error.message);
    
    // Only throw if it's a real auth error (401, 403)
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.error('❌ Auth error - refresh token invalid');
      throw new Error('REFRESH_TOKEN_INVALID');
    }
    
    // For other errors (network, timeout), throw but don't auto-logout
    throw error;
  }
};

// Request Interceptor - Check token expiry BEFORE sending request
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const accessToken = Cookies.get('accessToken');
    
    console.log('🔑 Token from cookies:', accessToken ? 'EXISTS' : 'MISSING');
    
    if (accessToken) {
      console.log('🔑 Token preview:', accessToken.substring(0, 20) + '...');
      
      // CHECK IF TOKEN IS EXPIRED BEFORE SENDING REQUEST
      if (isTokenExpired(accessToken)) {
        console.log('⏰ Token is expired, refreshing before request...');
        
        // If already refreshing, wait for the same promise
        if (isRefreshing && refreshPromise) {
          console.log('⏳ Already refreshing, waiting for existing promise...');
          try {
            const newToken = await refreshPromise;
            if (config.headers) {
              config.headers.Authorization = `Bearer ${newToken}`;
            }
            return config;
          } catch (error) {
            // If refresh failed, check if it's auth error
            if (error instanceof Error && error.message === 'REFRESH_TOKEN_INVALID') {
              clearAuthAndRedirect();
            }
            throw error;
          }
        }
        
        isRefreshing = true;
        
        // Create a single refresh promise that all requests will share
        refreshPromise = refreshAccessToken();
        
        try {
          const newToken = await refreshPromise;
          processQueue(null, newToken);
          
          if (config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
          console.log('✅ Using refreshed token for request');
          return config;
        } catch (error) {
          console.error('❌ Token refresh failed in request interceptor');
          processQueue(error as AxiosError, null);
          
          // Only clear auth if it's an auth error
          if (error instanceof Error && error.message === 'REFRESH_TOKEN_INVALID') {
            clearAuthAndRedirect();
          }
          
          throw error;
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      } else {
        // Token is valid, use it
        if (config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`;
          console.log('✅ Using valid token');
        }
      }
    }
    
    // Handle FormData (file uploads)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      console.log('📎 FormData detected, letting browser set Content-Type');
    } else if (config.headers && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor - Handle 401 errors as fallback
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ Response received:', response.config.url, response.status);
    
    // CHECK FOR TOKEN ERROR IN RESPONSE
    const data: any = response.data;
    
    // Check if response indicates token error
    if (data?.error) {
      const errorMsg = data.error.toLowerCase();
      
      // Token-related error messages
      if (
        errorMsg.includes('token') ||
        errorMsg.includes('expired') ||
        errorMsg.includes('invalid') ||
        errorMsg.includes('unauthorized')
      ) {
        console.error('❌ Token error in response:', data.error);
        const error: any = new Error(data.error);
        error.response = response;
        error.response.status = 401;
        throw error;
      }
    }
    
    return response;
  },
  async (error: AxiosError) => {
    console.error('❌ Response error:', error.response?.status, error.config?.url);
    console.error('❌ Error data:', error.response?.data);
    
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors as fallback
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔄 Got 401, attempting token refresh...');
      
      // If already refreshing, wait for the same promise
      if (isRefreshing && refreshPromise) {
        console.log('⏳ Already refreshing, waiting for existing promise...');
        try {
          const newToken = await refreshPromise;
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          if (refreshError instanceof Error && refreshError.message === 'REFRESH_TOKEN_INVALID') {
            clearAuthAndRedirect();
          }
          return Promise.reject(refreshError);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshPromise = refreshAccessToken();

      try {
        const newToken = await refreshPromise;
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError: any) {
        console.error('❌ Token refresh failed in response interceptor');
        processQueue(refreshError as AxiosError, null);
        
        // Only clear auth if it's an auth error
        if (refreshError instanceof Error && refreshError.message === 'REFRESH_TOKEN_INVALID') {
          clearAuthAndRedirect();
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;